import {
  cloneElement,
  isValidElement,
  useEffect,
  useId,
  type CSSProperties,
  type ReactElement,
  type ReactNode,
} from 'react';
import {
  Box,
  Card,
  CardBody,
  CardHeader,
  Container,
  Flex,
  Focusable,
  FullPage,
  Header,
  Skeleton,
  Text,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  TooltipTrigger,
  type HeaderBreadcrumb,
  type HeaderProps,
} from '@backstage/ui';
import { configApiRef, useApi } from '@backstage/frontend-plugin-api';

/**
 * The BUI building blocks the Internal Apps pages and the engineer-facing
 * technical view are composed from. They keep the look of the Ant Design
 * originals (Card, Tag, Statistic, Segmented, Spin, Empty) on BUI components
 * and semantic tokens, so each page only swaps components and keeps its
 * layout.
 */

/** Small uppercase "eyebrow" label. The source copy stays sentence-case; the
 *  all-caps look is purely visual (per the Make UX-writing style guide, which
 *  forbids typing in caps for emphasis but allows styling).
 *
 *  Shared by the plain app page and the engineer-facing technical view so the
 *  two stay visually in step. */
export const EYEBROW: CSSProperties = {
  display: 'block',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  fontSize: 11,
  fontWeight: 600,
};

/** Monospace in BUI's font stack, for versions, hashes and log lines. */
export const MONO: CSSProperties = { fontFamily: 'var(--bui-font-monospace)' };

/** BUI 0.18's Flex has no wrap prop; toolbars and tag rows wrap. */
export const WRAP: CSSProperties = { flexWrap: 'wrap' };

/** Links in the brand colour without an underline, as the antd theme had. */
export const BRAND_LINK: CSSProperties = { color: 'var(--make-fg-brand)' };

/** One headline number in a KPI / summary strip. */
export function Kpi({
  label,
  children,
  sub,
}: {
  label: ReactNode;
  children: ReactNode;
  sub?: ReactNode;
}) {
  return (
    <div>
      <Text as="span" color="secondary" style={EYEBROW}>
        {label}
      </Text>
      <div
        style={{
          fontSize: 22,
          fontWeight: 600,
          lineHeight: 1.25,
          marginTop: 6,
        }}
      >
        {children}
      </div>
      {sub && (
        <div style={{ marginTop: 4 }}>
          <Text variant="body-small" color="secondary">
            {sub}
          </Text>
        </div>
      )}
    </div>
  );
}

/** Sidebar section label, same eyebrow styling as the KPI labels. */
export function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <Text as="span" color="secondary" style={{ ...EYEBROW, marginBottom: 6 }}>
      {children}
    </Text>
  );
}

/**
 * Explanatory hover/focus text on a non-interactive element (antd Tooltip).
 * The trigger gets a tab stop so the explanation is reachable without a
 * pointer. Pass `asTrigger` when the child is already a focusable DOM element.
 */
export function Hint({
  tip,
  children,
  asTrigger = false,
}: {
  tip?: ReactNode;
  children: ReactNode;
  asTrigger?: boolean;
}) {
  if (!tip) return <>{children}</>;
  return (
    <TooltipTrigger delay={300}>
      <Focusable>
        {asTrigger ? (
          (children as ReactElement)
        ) : (
          <span style={{ display: 'inline-flex', maxWidth: '100%' }}>
            {children}
          </span>
        )}
      </Focusable>
      <Tooltip data-react-aria-top-layer style={{ maxWidth: 360 }}>
        {tip}
      </Tooltip>
    </TooltipTrigger>
  );
}

/**
 * The tag tones of the original antd Tags: `neutral` is the default grey tag,
 * `info` antd's blue/geekblue, `success` green, `warning` orange/gold,
 * `danger` red, and `brand` the purple tag.
 */
export type PillTone =
  | 'neutral'
  | 'info'
  | 'success'
  | 'warning'
  | 'danger'
  | 'brand';

const PILL_SURFACE: Record<PillTone, CSSProperties> = {
  neutral: {
    backgroundColor: 'var(--bui-bg-neutral-2)',
    border: '1px solid var(--bui-border-1)',
    color: 'var(--bui-fg-primary)',
  },
  info: {
    backgroundColor: 'var(--bui-bg-info)',
    border: '1px solid var(--bui-border-info)',
    color: 'var(--bui-fg-info)',
  },
  success: {
    backgroundColor: 'var(--bui-bg-success)',
    border: '1px solid var(--bui-border-success)',
    color: 'var(--bui-fg-success)',
  },
  warning: {
    backgroundColor: 'var(--bui-bg-warning)',
    border: '1px solid var(--bui-border-warning)',
    color: 'var(--bui-fg-warning)',
  },
  danger: {
    backgroundColor: 'var(--bui-bg-danger)',
    border: '1px solid var(--bui-border-danger)',
    color: 'var(--bui-fg-danger)',
  },
  brand: {
    backgroundColor: 'var(--make-bg-brand-subtle)',
    border: '1px solid var(--make-border-brand-subtle)',
    color: 'var(--make-fg-brand)',
  },
};

/** Sizes an icon for a pill, whichever icon set it comes from. */
function pillIcon(icon: ReactNode) {
  if (!isValidElement(icon)) return icon;
  const element = icon as ReactElement<{
    size?: number;
    'aria-hidden'?: boolean;
    style?: CSSProperties;
  }>;
  return cloneElement(element, {
    size: 12,
    'aria-hidden': true,
    style: { ...element.props.style, flexShrink: 0 },
  });
}

/**
 * The plugin's tag (antd Tag): a small bordered label with an optional icon,
 * tinted with BUI's status tokens or the shared brand tokens. `mono` is for
 * version hashes; `title` adds a hover/focus explanation.
 */
export function Pill({
  children,
  tone = 'neutral',
  icon,
  mono = false,
  round = false,
  compact = false,
  title,
}: {
  children: ReactNode;
  tone?: PillTone;
  icon?: ReactNode;
  mono?: boolean;
  /** Fully rounded, as the access and status pills. */
  round?: boolean;
  /** Tighter, for a pill beside a link in a dense list. */
  compact?: boolean;
  title?: ReactNode;
}) {
  const pill = (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--bui-space-1)',
        maxWidth: '100%',
        padding: round ? `0 ${compact ? 8 : 10}px` : '0 7px',
        borderRadius: round ? 'var(--bui-radius-full)' : 'var(--bui-radius-2)',
        fontSize: compact ? 11 : 'var(--bui-font-size-2)',
        lineHeight: compact ? '18px' : '20px',
        whiteSpace: 'nowrap',
        ...PILL_SURFACE[tone],
        ...(mono ? MONO : {}),
      }}
    >
      {icon ? pillIcon(icon) : null}
      {children}
    </span>
  );
  return title ? <Hint tip={title}>{pill}</Hint> : pill;
}

/**
 * A titled card (antd Card): a bold title with an optional right-hand extra,
 * a divider, then the content. `noPadding` lets a table run to the edges.
 */
export function Panel({
  title,
  extra,
  children,
  noPadding = false,
  id,
  style,
}: {
  title?: ReactNode;
  extra?: ReactNode;
  children: ReactNode;
  noPadding?: boolean;
  id?: string;
  style?: CSSProperties;
}) {
  const titleId = useId();
  return (
    <Card
      id={id}
      role={title ? 'region' : undefined}
      aria-labelledby={title ? titleId : undefined}
      style={style}
    >
      {(title || extra) && (
        <CardHeader
          style={{
            padding: 'var(--bui-space-4) var(--bui-space-6)',
            borderBottom: '1px solid var(--bui-border-1)',
          }}
        >
          <Flex justify="between" align="center" gap="4" style={WRAP}>
            {title ? (
              <Text as="h2" variant="body-large" weight="bold" id={titleId}>
                {title}
              </Text>
            ) : (
              <span />
            )}
            {extra}
          </Flex>
        </CardHeader>
      )}
      <CardBody style={{ padding: noPadding ? 0 : 'var(--bui-space-6)' }}>
        {children}
      </CardBody>
    </Card>
  );
}

/**
 * Sets the browser-tab title while the page is shown, as core-components'
 * <Header> did; BUI's <Header> does not touch the title.
 */
export function usePageTitle(pageTitle: string) {
  const appTitle =
    useApi(configApiRef).getOptionalString('app.title') ?? 'Backstage';
  useEffect(() => {
    const previous = document.title;
    document.title = `${pageTitle} | ${appTitle}`;
    return () => {
      document.title = previous;
    };
  }, [pageTitle, appTitle]);
}

/**
 * The page shell (core-components Page + Header + Content): BUI's page header
 * with optional breadcrumbs, description and right-hand actions, then the
 * content in a container.
 */
export function PageShell({
  title,
  breadcrumbs,
  description,
  actions,
  tabs,
  activeTabId,
  children,
}: {
  title: string;
  breadcrumbs?: HeaderBreadcrumb[];
  description?: string;
  actions?: ReactNode;
  tabs?: HeaderProps['tabs'];
  activeTabId?: string | null;
  children: ReactNode;
}): ReactElement {
  usePageTitle(title);
  return (
    <FullPage>
      <Header
        title={title}
        breadcrumbs={breadcrumbs}
        description={description}
        customActions={actions}
        tabs={tabs}
        activeTabId={activeTabId}
      />
      <Container py="6">{children}</Container>
    </FullPage>
  );
}

/**
 * A segmented control (antd Segmented): a single-selection toggle group whose
 * selected item is filled, as the brand-themed Segmented was.
 */
export function Segmented<T extends string>({
  options,
  value,
  onChange,
  'aria-label': ariaLabel,
  size = 'small',
}: {
  options: ReadonlyArray<{ value: T; label: ReactNode; isDisabled?: boolean }>;
  value: T;
  onChange: (value: T) => void;
  'aria-label': string;
  size?: 'small' | 'medium';
}) {
  return (
    <ToggleButtonGroup
      selectionMode="single"
      disallowEmptySelection
      selectedKeys={[value]}
      onSelectionChange={keys => {
        const [next] = [...keys];
        if (next !== undefined && next !== value) onChange(next as T);
      }}
      aria-label={ariaLabel}
    >
      {options.map(option => (
        <ToggleButton
          key={option.value}
          id={option.value}
          size={size}
          isDisabled={option.isDisabled}
        >
          {option.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}

/** A loading placeholder (antd Spin) on BUI's animated Skeleton. */
export function Loading({
  label = 'Loading',
  lines = 3,
}: {
  label?: string;
  lines?: number;
}) {
  return (
    <Flex
      direction="column"
      gap="2"
      role="progressbar"
      aria-label={label}
      aria-busy="true"
    >
      {Array.from({ length: lines }, (_, i) => (
        <Skeleton
          key={i}
          width={i === lines - 1 ? '60%' : '100%'}
          height={16}
          rounded
        />
      ))}
    </Flex>
  );
}

/** A centred empty state (antd Empty): a muted message and optional action. */
export function EmptyBlock({
  children,
  action,
}: {
  children: ReactNode;
  action?: ReactNode;
}) {
  return (
    <Flex direction="column" align="center" gap="3" py="6">
      <Text color="secondary" style={{ textAlign: 'center' }}>
        {children}
      </Text>
      {action}
    </Flex>
  );
}

/** A count bubble (antd Badge count) next to a label. */
export function CountBadge({
  count,
  tone = 'brand',
}: {
  count: number;
  tone?: 'brand' | 'neutral';
}) {
  return (
    <Box
      as="span"
      style={{
        display: 'inline-block',
        minWidth: 20,
        height: 20,
        padding: '0 6px',
        boxSizing: 'border-box',
        borderRadius: 'var(--bui-radius-full)',
        fontSize: 'var(--bui-font-size-2)',
        lineHeight: '20px',
        textAlign: 'center',
        ...(tone === 'brand'
          ? {
              backgroundColor: 'var(--bui-bg-solid)',
              color: 'var(--bui-fg-solid)',
            }
          : {
              // A solid grey bubble, as antd's default count Badge.
              backgroundColor: 'var(--bui-fg-secondary)',
              color: 'var(--bui-bg-neutral-1)',
            }),
      }}
    >
      {count}
    </Box>
  );
}
